import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

// Alert types
export const ALERT_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning'
};

export const alertSlice = createSlice({
  name: 'alert',
  initialState: {
    // array of type [{ id: 'some-id-1', message: 'some message', type: 'error', autoClose: false }, ...]
    alerts: [],
  },
  reducers: {
    clearAlerts: (state) => {
      state.alerts = []
    },
    setAlerts: (state, action) => {
      state.alerts = action.payload;
    },
    /**
     * This reducer is supposed to be used to handle errors coming from the backend.
     */
    handleError: {
      reducer: (state, action) => {
        try {
          if(action.payload){
            const { id: errorId } = action.payload;
            const { userFriendlyMessages } = action.payload;
            const newAlerts = userFriendlyMessages.map((m, i) => ({
              id: `${errorId}+${i}`,
              message: m,
              type: ALERT_TYPES.ERROR,
              autoClose: false
            }));
    
            state.alerts = [...state.alerts, ...newAlerts];
          }
        } catch (error) {
          const errorId = parseInt(Math.random() * 1000)
          state.alerts = [...state.alerts, ...[{
            id: errorId,
            message: error.message,
            type: ALERT_TYPES.ERROR,
            autoClose: false
          }]];
        }
      },
      prepare: error => {
        console.log(error)

        // Skip showing alerts for 402 (Payment Required) errors
        // These are handled by specific UI components (e.g., PayToUse modal)
        if (error?.status?.code === 402) {
          return { payload: null }
        }

        const errorDescription = {
          id: uuidv4(),
          userFriendlyMessages: error.userFriendlyMessages || [error.message]
        }

        return { payload: errorDescription }
      }
    },
    showAlert: {
      reducer: (state, action) => {
        state.alerts = [...state.alerts, action.payload];
        
        // If the alert is set to auto-close, we'll handle that in the component
      },
      prepare: (message, options = {}) => {
        const alertData = {
          id: uuidv4(),
          message,
          type: options.type || ALERT_TYPES.INFO,
          autoClose: options.autoClose !== undefined ? options.autoClose : 
                    (options.type === ALERT_TYPES.SUCCESS), // Auto-close success by default
          duration: options.duration || 5000
        };
        
        return { payload: alertData };
      }
    },
    showSuccessAlert: {
      reducer: (state, action) => {
        state.alerts = [...state.alerts, action.payload];
      },
      prepare: (message, options = {}) => {
        const alertData = {
          id: uuidv4(),
          message,
          type: ALERT_TYPES.SUCCESS,
          autoClose: options.autoClose !== undefined ? options.autoClose : true,
          duration: options.duration || 5000
        };
        
        return { payload: alertData };
      }
    },
    showInfoAlert: {
      reducer: (state, action) => {
        state.alerts = [...state.alerts, action.payload];
      },
      prepare: (message, options = {}) => {
        const alertData = {
          id: uuidv4(),
          message,
          type: ALERT_TYPES.INFO,
          autoClose: options.autoClose !== undefined ? options.autoClose : true,
          duration: options.duration || 5000
        };
        
        return { payload: alertData };
      }
    },
    showWarningAlert: {
      reducer: (state, action) => {
        state.alerts = [...state.alerts, action.payload];
      },
      prepare: (message, options = {}) => {
        const alertData = {
          id: uuidv4(),
          message,
          type: ALERT_TYPES.WARNING,
          autoClose: options.autoClose !== undefined ? options.autoClose : true,
          duration: options.duration || 5000
        };
        
        return { payload: alertData };
      }
    },
    removeAlert(state, action) {
      const removeId = action.payload;
      const filtered = state.alerts.filter(a => a.id !== removeId);
      state.alerts = [...filtered];
    }
  },
});

export const { 
  clearAlerts, 
  setAlerts, 
  handleError, 
  showAlert,
  showSuccessAlert,
  showInfoAlert,
  showWarningAlert,
  removeAlert 
} = alertSlice.actions;

export default alertSlice.reducer;
